import React, { useState, useEffect, useRef } from "react"; 
import { useParams, useNavigate } from "react-router-dom"; 
import { ArrowLeft, X, CheckCircle, AlertCircle, Plus, Minus, Sparkles, ChevronDown } from "lucide-react"; 
import { motion, AnimatePresence } from "framer-motion"; 
import flowerData from "./Data.json"; 
import { colorMeanings, flowerMeaningsByType } from "./MatNguData.js"; 
import bannerMauSacImg from "./assets/matngu/banner-mau-sac.webp"; // Ảnh cho banner theo màu sắc
import bannerYNghiaImg from "./assets/matngu/banner-y-nghia.webp"; // Ảnh cho banner theo ý nghĩa

// ========================================== 
// IMPORT DANH SÁCH HOA ĐƠN MỞ RỘNG TỪ ASSETS 
// ========================================== 
import hoaHongImg from "./assets/matngu/hoa-hong.webp"; 
import camTuCauImg from "./assets/matngu/cam-tu-cau.webp"; 
import huongDuongImg from "./assets/matngu/huong-duong.webp"; 
import mauDonImg from "./assets/matngu/mau-don.webp"; 
import tulipImg from "./assets/matngu/tulip.webp"; 
import linhLanImg from "./assets/matngu/linh-lan.webp"; 
import bachHopImg from "./assets/matngu/bach-hop.webp"; 
import oaiHuongImg from "./assets/matngu/oai-huong.webp"; 
import cucHoaMiImg from "./assets/matngu/cuc-hoa-mi.webp"; 

// ========================================== 
// IMPORT ẢNH NỀN KỂ CHUYỆN (STORYTELLING) THEO MÀU 
// ========================================== 
import storyDo from "./assets/matngu/story-do.webp"; 
import storyHong from "./assets/matngu/story-hong.webp"; 
import storyTrang from "./assets/matngu/story-trang.webp"; 
import storyVang from "./assets/matngu/story-vang.webp"; 
import storyTim from "./assets/matngu/story-tim.webp"; 
import storyXanhLa from "./assets/matngu/story-xanh-la.webp"; 
import storyXanhDuong from "./assets/matngu/story-xanh-duong.webp"; 
import storyCam from "./assets/matngu/story-cam.webp"; 

const singleFlowerImages = { 
  "Hoa hồng": hoaHongImg, 
  "Hoa cẩm tú cầu": camTuCauImg, 
  "Hoa hướng dương": huongDuongImg, 
  "Hoa mẫu đơn": mauDonImg, 
  "Hoa tulip": tulipImg, 
  "Hoa linh lan": linhLanImg, 
  "Hoa bách hợp": bachHopImg, 
  "Hoa oải hương": oaiHuongImg, 
  "Hoa cúc họa mi": cucHoaMiImg, 
}; 

const colorStoryImages = { 
  "Đỏ": storyDo, 
  "Hồng": storyHong, 
  "Trắng": storyTrang, 
  "Vàng": storyVang, 
  "Tím": storyTim, 
  "Xanh Lá": storyXanhLa, 
  "Xanh Dương": storyXanhDuong, 
  "Cam": storyCam, 
}; 

// Phân đoạn thơ văn tự sự cao cấp cho từng tone màu đại diện phong cách Apple Storytelling 
const colorPoetry = { 
  "Đỏ": "Bản tuyên ngôn vĩnh cửu của những trái tim cuồng nhiệt, sắc đỏ đánh thức đam mê sâu thẳm kiêu sa.", 
  "Hồng": "Nốt nhạc dịu dàng dệt nên giấc mộng ban sơ, hiện thân của tình cảm thuần khiết và lòng biết ơn tinh tế.", 
  "Trắng": "Khoảng lặng tinh khôi tôn vinh lòng kiên định, sắc trắng lắng đọng dòng thời gian sang trọng vô ngần.", 
  "Vàng": "Hào quang ấm áp tựa vạt nắng hoàng kim, khơi gợi niềm kiêu hãnh, thịnh vượng và tri kỷ sắt son.", 
  "Tím": "Sự thanh lịch ẩn mình trong màn sương huyền bí, sắc tím thủy chung mang hơi thở vương giả quý phái.", 
  "Xanh Lá": "Giai điệu an lành của thiên nhiên nguyên bản, thanh lọc tâm hồn bằng may mắn và sức sống căng tràn.", 
  "Xanh Dương": "Biển trời bao la đúc kết niềm tin vững chãi, sắc xanh thanh bình khơi nguồn trí tuệ vô hạn.", 
  "Cam": "Ngọn lửa năng lượng tràn đầy cảm hứng, sắc cam rực rỡ sưởi ấm mọi không gian bằng sự nồng hậu." 
}; 

// Map bảng màu cao cấp đồng bộ chuyển đổi động theo từng Tone màu được chọn 
const colorPalette = { 
  "Đỏ": { bg: "#FFF5F5", text: "#1A0000", tabActive: "#A83232", accent: "#5C1313" }, 
  "Hồng": { bg: "#FFF5F9", text: "#1A000D", tabActive: "#B45CA1", accent: "#6B2A5B" }, 
  "Trắng": { bg: "#F9F9F9", text: "#111111", tabActive: "#111111", accent: "#555555" }, 
  "Vàng": { bg: "#FFFDF2", text: "#1A1500", tabActive: "#C9A644", accent: "#735B18" }, 
  "Tím": { bg: "#F8F6FA", text: "#11001A", tabActive: "#7C6494", accent: "#423252" }, 
  "Xanh Lá": { bg: "#F4FAF6", text: "#001A0A", tabActive: "#007930", accent: "#00471C" }, 
  "Xanh Dương": { bg: "#F2F8FC", text: "#00111A", tabActive: "#00678d", accent: "#003B52" }, 
  "Cam": { bg: "#FFF7F4", text: "#1A0800", tabActive: "#98360f", accent: "#5C1D05" }, 
}; 

const defaultPalette = { bg: "#F4F5F1", text: "#2C302E", tabActive: "#111111", accent: "#444444" }; 

// Inject Font chữ và Style cao cấp đồng bộ với phong cách Floraison / Ren Florist 
const globalStyles = ` 
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Montserrat:wght@200;300;400;500;600&display=swap'); 
.luxury-serif { font-family: 'Cormorant Garamond', serif !important; } 
.luxury-sans { font-family: 'Montserrat', sans-serif !important; } 
.smooth-transition { transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1); } 
.img-overflow-hidden { overflow: hidden; position: relative; } 
.custom-scrollbar::-webkit-scrollbar { width: 4px; } 
.custom-scrollbar::-webkit-scrollbar-track { background: #fdfdfd; } 
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e0e0e0; } 
.apple-blur { backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); } 
`; 

// ========================================== 
// TOAST NOTIFICATION COMPONENT 
// ========================================== 
export const ToastNotification = ({ toast, setToast }) => {
  return (
    <AnimatePresence>
      {toast.show && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          style={styles.toastWrapper}
        >
          <div style={styles.toastContainer}>
            {toast.message}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 

export const useToast = () => { 
  const [toast, setToast] = useState({ show: false, message: "", type: "success" }); 
  const showToast = (message, type = "success") => { 
    setToast({ show: true, message, type }); 
  }; 
  useEffect(() => { 
    if (toast.show) { 
      const timer = setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 3000); 
      return () => clearTimeout(timer); 
    } 
  }, [toast.show]); 
  return { toast, setToast, showToast }; 
}; 

// ========================================== 
// MAIN COMPONENT 
// ========================================== 
export default function MatNguLoaiHoa() {
  const { type, flowerSlug } = useParams();
  const navigate = useNavigate(); 
  const [selectedColor, setSelectedColor] = useState("Đỏ"); 
  const [selectedFlower, setSelectedFlower] = useState(null); 
  const { toast, setToast, showToast } = useToast(); 
  const [openSection, setOpenSection] = useState("giao-hang"); 
  const [meaningStep, setMeaningStep] = useState("single-flowers"); 
  const [chosenSingleFlowerKey, setChosenSingleFlowerKey] = useState(null); 
  const activePalette = colorPalette[selectedColor] || defaultPalette; 
  const [hoveredId, setHoveredId] = useState(null); 
  const collectionRef = useRef(null); 

  useEffect(() => {
    if (!type) {
      navigate("/mat-ngu-loai-hoa/theo-y-nghia");
    }
  }, [type, navigate]);

  // Xử lý URL chi tiết hoa khi page load
  useEffect(() => {
    if (flowerSlug) {
      const foundFlower = flowerData.find(hoa => {
        const slug = standardSlugify(hoa["Tên hoa"] || hoa.name);
        return slug === flowerSlug;
      });
      if (foundFlower) {
        setSelectedFlower(foundFlower);
      }
    }
  }, [flowerSlug]);

  useEffect(() => {
    document.body.style.overflow = selectedFlower ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [selectedFlower]); 

  const standardSlugify = (str) => { 
    if (!str) return ""; 
    return str .toLowerCase() .normalize("NFD") .replace(/[\u0300-\u036f]/g, "") .replace(/[đĐ]/g, "d") .replace(/([^0-9a-z-\s])/g, "") .replace(/(\s+|-+)/g, "-") .trim(); 
  }; 

  const openPopup = (flower) => { 
    setSelectedFlower(flower); 
    const urlName = standardSlugify(flower["Tên hoa"] || flower.name); 
    window.history.pushState(null, "", `/mat-ngu-loai-hoa/${type}/${urlName}`); 
  }; 

  const closePopup = () => { 
    setSelectedFlower(null); 
    window.history.pushState(null, "", `/mat-ngu-loai-hoa/${type}`); 
  }; 

  useEffect(() => { 
    const handlePopState = () => setSelectedFlower(null); 
    window.addEventListener("popstate", handlePopState); 
    return () => window.removeEventListener("popstate", handlePopState); 
  }, []); 

  const addToCart = (hoa) => {
    try {
      const cart = JSON.parse(localStorage.getItem("sellixy_cart")) || [];
      const flowerId = hoa["Mã hoa"] || hoa.id || `flower-${standardSlugify(hoa.name)}`;
      const flowerName = hoa["Tên hoa"] || hoa.name;
      const rawPrice = hoa["Giá bán"] || hoa.price || "0";
      const cleanPrice = rawPrice.toString().replace(/\D/g, "");
      const numericPrice = parseInt(cleanPrice) || 0;
      const existingItem = cart.find((item) => item.id === flowerId);
      if (existingItem) {
        existingItem.quantity = (existingItem.quantity || 1) + 1;
      } else {
        cart.push({ id: flowerId, name: flowerName, price: numericPrice, img: hoa["Mã hoa"] ? `/flowers/${hoa["Mã hoa"]}` : `/flowers/${hoa.image}`, ma: flowerId, quantity: 1, });
      }
      localStorage.setItem("sellixy_cart", JSON.stringify(cart));
      window.dispatchEvent(new Event("cartUpdated"));
      showToast(`✓ Đã thêm vào giỏ hàng thành công!`, "success");
    } catch (error) {
      console.error("Lỗi khi thêm vào giỏ hàng:", error);
      showToast("✕ Có lỗi xảy ra, vui lòng thử lại!", "error");
    }
  }; 

  const formatPrice = (price) => { 
    if (!price) return "Liên hệ Floraison"; 
    const cleanPrice = price.toString().replace(/\D/g, ""); 
    if (!cleanPrice) return "Liên hệ Floraison"; 
    return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(cleanPrice); 
  }; 

  const filteredFlowers = flowerData.filter((hoa) => { 
    if (!hoa) return false; 
    const tenHoa = hoa["Tên hoa"]?.toString().trim() || ""; 
    const yNghia = hoa["Ý nghĩa của hoa"]?.toString().trim() || ""; 
    const maChua = hoa["Mã hoa"]?.toString().trim() || ""; 
    const isValidMaHoa = maChua && /^flower\d+\.(png|avif|webp|jpg)$/i.test(maChua); 
    return tenHoa && yNghia && isValidMaHoa && tenHoa !== "Chưa cập nhật tên" && !yNghia.includes("Đang cập nhật"); 
  }); 

  const getFlowersByColor = (color) => { 
    return filteredFlowers.filter((hoa) => hoa["Tông màu hoa"]?.toLowerCase().includes(color.toLowerCase())); 
  }; 

  const getFlowersByType = (typeKey) => { 
    const keyword = typeKey.replace("Hoa ", "").toLowerCase(); 
    return filteredFlowers.filter((hoa) => { 
      const name = (hoa["Tên hoa"] || "").toLowerCase(); 
      const component = (hoa["Thành phần hoa"] || "").toLowerCase(); 
      if (typeKey === "Hoa bách hợp") { 
        return name.includes("ly") || component.includes("ly") || name.includes("bách hợp"); 
      } 
      if (typeKey === "Hoa cúc họa mi") { 
        return name.includes("cúc") || component.includes("cúc"); 
      } 
      return name.includes(keyword) || component.includes(keyword); 
    }); 
  }; 

  const scrollToCollection = () => { 
    collectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); 
  }; 

  const colors = Object.keys(colorMeanings); 
  const currentFlowers = getFlowersByColor(selectedColor); 
  const currentTypeFlowers = chosenSingleFlowerKey ? getFlowersByType(chosenSingleFlowerKey) : []; 

  const renderFormattedMeaning = (text) => { 
    if (!text) return null; 
    const paragraphs = text.split("\n"); 
    return paragraphs.map((para, idx) => { 
      if (!para.trim()) return null; 
      if (para.trim().toLowerCase().startsWith("thích hợp")) { 
        return <p key={idx} style={{ ...styles.suitableBox, borderColor: activePalette.tabActive }} className="luxury-sans">✦ {para}</p>; 
      } 
      return <p key={idx} style={{ ...styles.meaningText, color: activePalette.text }} className="luxury-serif">{para}</p>; 
    }); 
  }; 

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }; 
  const itemVariants = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } }; 

  return (
  <div style={styles.container}>
    <style>{globalStyles}</style>
    <style>{`
      /* ========== RESPONSIVE DESIGN FOR MOBILE & TABLET ========== */

      /* Mobile: Small phones (< 480px) */
      @media (max-width: 480px) {
        .hero-section, .appleHeroBanner, .meaningHeroBanner1 {
          height: 60vh !important;
        }

        .heroMainTitle, .meaningHeroTitle {
          font-size: 24px !important;
          line-height: 1.2 !important;
        }

        .heroSubText, .meaningHeroSub {
          font-size: 12px !important;
        }

        .hero-mini-tag {
          font-size: 9px !important;
        }

        .luxury-serif {
          font-size: 16px !important;
        }

        .collection-divider {
          padding: 40px 5% 20px 5% !important;
        }

        .divider-text {
          font-size: 11px !important;
        }

        .studio-grid, .single-flower-grid, .bouquet-grid {
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 2px !important;
          padding: 0 5% 40px 5% !important;
        }

        .single-flower-grid {
          grid-template-columns: 1fr !important;
        }

        .summary-section-title {
          font-size: 16px !important;
        }

        .tabs-wrapper {
          gap: 10px 15px !important;
        }

        .tab-btn {
          font-size: 11px !important;
        }

        .editorial-row {
          grid-template-columns: 1fr !important;
          gap: 30px !important;
          padding: 0 5% !important;
        }

        .editorial-img-col {
          order: -1;
        }

        .story-section-background {
          padding: 40px 5% !important;
        }

        .editorial-title {
          font-size: 28px !important;
        }

        .editorial-poetry {
          font-size: 14px !important;
        }

        .apple-cta-btn {
          font-size: 10px !important;
          padding: 10px 20px !important;
        }

        .apple-hero-banner, .meaningHeroBanner1 {
          min-height: 60vh !important;
        }

        .header {
          padding: 20px 5% !important;
        }

        .back-btn {
          left: 5% !important;
          font-size: 11px !important;
        }

        .modal-content {
          flex-direction: column !important;
          max-width: 95vw !important;
          height: auto !important;
          max-height: 90vh !important;
          padding: 20px !important;
        }

        .modal-image-column {
          flex: 0 0 auto !important;
          height: 280px !important;
          padding: 15px !important;
          margin-bottom: 20px !important;
        }

        .modal-info-column {
          flex: 1 1 auto !important;
          padding: 20px !important;
          overflow-y: auto !important;
          max-height: calc(90vh - 300px) !important;
        }

        .modal-title {
          font-size: 22px !important;
        }

        .modal-desc-text {
          font-size: 12px !important;
        }

        .add-to-cart-btn {
          font-size: 11px !important;
          padding: 12px 0 !important;
        }

        .accordion-header {
          font-size: 11px !important;
        }

        .meaning-editorial-header {
          padding: 40px 5% !important;
        }

        .mini-flower-thumb-grid {
          gap: 10px !important;
        }

        .mini-flower-thumb-btn {
          padding: 6px 12px !important;
          font-size: 10px !important;
        }

        .toast-container {
          font-size: 12px !important;
          padding: 12px 20px !important;
          max-width: 90vw !important;
        }
      }

      /* Tablet: Medium devices (480px - 768px) */
      @media (max-width: 768px) and (min-width: 480px) {
        .appleHeroBanner, .meaningHeroBanner1 {
          height: 70vh !important;
        }

        .heroMainTitle, .meaningHeroTitle {
          font-size: 32px !important;
        }

        .heroSubText, .meaningHeroSub {
          font-size: 13px !important;
        }

        .studio-grid, .bouquet-grid {
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 3px !important;
          padding: 0 5% 60px 5% !important;
        }

        .single-flower-grid {
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 3px !important;
          padding: 0 5% !important;
        }

        .collection-divider {
          padding: 50px 5% 25px 5% !important;
        }

        .editorial-row {
          grid-template-columns: 1fr 1fr !important;
          gap: 40px !important;
          padding: 40px 5% !important;
        }

        .story-section-background {
          padding: 60px 5% !important;
        }

        .editorial-title {
          font-size: 32px !important;
        }

        .summary-section-title {
          font-size: 20px !important;
        }

        .tabs-wrapper {
          gap: 15px 25px !important;
        }

        .meaning-editorial-header {
          padding: 50px 5% !important;
        }

        .modal-content {
          flex-direction: column !important;
          max-width: 90vw !important;
          max-height: 90vh !important;
        }

        .modal-image-column {
          height: 320px !important;
          padding: 25px !important;
          margin-bottom: 25px !important;
        }

        .modal-title {
          font-size: 28px !important;
        }

        .toast-container {
          font-size: 13px !important;
          padding: 13px 24px !important;
        }
      }

      /* Large Tablet & Desktop: 768px+ */
      @media (min-width: 768px) {
        .studio-grid, .bouquet-grid {
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)) !important;
        }

        .single-flower-grid {
          grid-template-columns: repeat(3, 1fr) !important;
        }

        .editorial-row {
          grid-template-columns: 1.1fr 0.9fr !important;
          gap: 80px !important;
        }
      }
    `}</style>

    {/* ========================================== VIEW 1: THEO MÀU SẮC (STORYTELLING EDITION) ========================================== */}
    {type === "theo-mau" && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
        
        {/*BANNER 1: INTRO TO TONE - Đã chỉnh sửa để tự nhận ảnh import */}
        <div style={{ ...styles.appleHeroBanner, backgroundImage: `url(${bannerMauSacImg})` }}>
          <div style={styles.heroOverlayDark} />
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 1, ease: "easeOut" }} 
            style={styles.heroTextCenter}
          >
            <span className="luxury-sans" style={styles.heroMiniTag}>bản giao hương sắc màu</span>
            <h1 className="luxury-serif" style={styles.heroMainTitle}>MẬT NGỮ SẮC HOA</h1>
            <p className="luxury-sans" style={styles.heroSubText}>Mỗi sắc thái tinh tuyển là một câu chuyện chương hồi được viết nên bởi tạo hóa</p>
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} style={{ marginTop: '30px' }}>
              <ChevronDown size={20} color="#ffffff" strokeWidth={1} />
            </motion.div>
          </motion.div>
        </div>

        {/* Header thanh lịch tối giản định vị */}
        <div style={styles.header}>
          <motion.button onClick={() => navigate("/mat-ngu-loai-hoa/theo-y-nghia")} style={styles.backBtn} whileHover={{ x: -4 }}>
            <ArrowLeft size={13} />
            <span className="luxury-sans">Thư viện mật ngữ ý nghĩa</span>
          </motion.button>
          <div style={{ width: "100px" }} />
        </div>

        {/* SECTION THANH TABS NGANG ĐỒNG BỘ APPLE NAV BLOCK */}
        <div style={styles.appleSummaryBlock}>
          <h3 className="luxury-serif" style={styles.summarySectionTitle}>TÌM THEO CẢM HỨNG SẮC HOA</h3>
          <div style={styles.tabsWrapper}>
            {colors.map((color) => {
              const isSelected = selectedColor === color;
              const tabColorConfig = colorPalette[color] || defaultPalette;
              return (
                <button 
                  key={color} 
                  onClick={() => { setSelectedColor(color); }} 
                  style={{ ...styles.tabBtn, color: isSelected ? tabColorConfig.tabActive : "#888888", fontWeight: isSelected ? "500" : "300" }} 
                  className="luxury-sans"
                >
                  {color}
                  {isSelected && (
                    <motion.div 
                      layoutId="activeColorLine" 
                      style={{ ...styles.activeTabLine, backgroundColor: tabColorConfig.tabActive }} 
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} 
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Phân đoạn Kể chuyện tích hợp Banner đổi sắc độ mượt mà */}
        <motion.div style={styles.storySectionBackground} animate={{ backgroundColor: activePalette.bg }} transition={{ duration: 0.8, ease: "easeInOut" }}>
          <AnimatePresence mode="wait">
            <motion.div 
              key={selectedColor} 
              initial={{ opacity: 0, scale: 0.98 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 1.02 }} 
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} 
              style={styles.editorialRow}
            >
              {/* Khối chữ tự sự bên trái */}
              <div style={styles.editorialTextCol}>
                <motion.span style={{ ...styles.editorialSub, color: activePalette.accent }} className="luxury-sans">
                  Chương {colors.indexOf(selectedColor) + 1}
                </motion.span>
                <motion.h2 style={{ ...styles.editorialTitle, color: activePalette.text }} className="luxury-serif">
                  Sắc {selectedColor}
                </motion.h2>
                <p style={{ ...styles.editorialPoetry, color: activePalette.accent }} className="luxury-serif italic">
                  "{colorPoetry[selectedColor]}"
                </p>
                <div style={styles.meaningBlock}>
                  {renderFormattedMeaning(colorMeanings[selectedColor])}
                </div>
                <motion.button 
                  onClick={scrollToCollection} 
                  style={{ ...styles.appleCtaBtn, color: activePalette.text, borderColor: activePalette.tabActive }} 
                  whileHover={{ scale: 1.03, x: 5 }} 
                  className="luxury-sans"
                >
                  Khám phá bộ sưu tập sắc {selectedColor}
                </motion.button>
              </div>

              {/* Khối ảnh minh họa tràn viền nghệ thuật */}
              <div style={styles.editorialImgCol}>
                <div style={styles.editorialImgFrame} className="img-overflow-hidden">
                  {colorStoryImages[selectedColor] ? (
                    <motion.img initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.2 }} src={colorStoryImages[selectedColor]} alt={`Cảm hứng sắc ${selectedColor}`} style={styles.editorialImg} />
                  ) : (
                    <div style={styles.editorialImgPlaceholder}>
                      <Sparkles size={32} color="#d4af37" strokeWidth={0.7} />
                    </div>
                  )}
                  <div style={styles.editorialTag} className="luxury-sans">Floraison</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Bộ sưu tập các thiết kế cao cấp thực tế */}
        <div ref={collectionRef} style={{ paddingTop: "60px" }}>
          <div style={styles.collectionDivider}>
            <div style={styles.dividerLine} />
            <span style={styles.dividerText} className="luxury-sans">TUYỆT TÁC HOA MÀU {selectedColor.toUpperCase()}</span>
            <div style={styles.dividerLine} />
          </div>
          <motion.div style={styles.studioGrid} variants={containerVariants} initial="hidden" animate="visible" key={`grid-${selectedColor}`}>
            {currentFlowers.length > 0 ? (
              currentFlowers.map((hoa) => (
                <motion.div 
                  key={hoa["Mã hoa"]} 
                  style={styles.studioCard} 
                  variants={itemVariants} 
                  onClick={() => openPopup(hoa)} 
                  onMouseEnter={() => setHoveredId(hoa["Mã hoa"])} 
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <div style={styles.cardImgContainer} className="img-overflow-hidden">
                    <motion.img
                      src={`/flowers/${hoa["Mã hoa"]}`}
                      alt={hoa["Tên hoa"]}
                      style={{ ...styles.cardImg, objectFit: "contain", width: "100%", height: "100%", padding: "24px", boxSizing: "border-box" }}
                      animate={{ scale: hoveredId === hoa["Mã hoa"] ? 1.04 : 1 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    />
                    <div style={styles.cardBadge} className="luxury-sans"> MÀU {selectedColor}</div>
                    <AnimatePresence>
                      {hoveredId === hoa["Mã hoa"] && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} style={styles.cardHoverOverlay}>
                          <motion.div initial={{ y: 15 }} animate={{ y: 0 }} exit={{ y: 10 }} transition={{ duration: 0.4, ease: "easeOut" }}>
                            <h4 style={styles.hoverProductName} className="luxury-serif">{hoa["Tên hoa"]}</h4>
                            <div style={styles.hoverPriceContainer}>
                              <span style={styles.hoverPrice} className="luxury-sans">{formatPrice(hoa["Giá bán"])}</span>
                            </div>
                            <p style={styles.hoverCta} className="luxury-sans">Chiêm ngưỡng tác phẩm ✧</p>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))
            ) : (
              <p style={styles.emptyText} className="luxury-sans">Nghệ nhân đang phát triển thêm thiết kế độc bản cho màu này.</p>
            )}
          </motion.div>
        </div>
      </motion.div>
    )}

    {/* ========================================== VIEW 2: THEO Ý NGHĨA (CINEMATIC FLOW 1 BANNER CAO CẤP) ========================================== */}
    {type === "theo-y-nghia" && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
        {/* ĐÃ CHỈNH SỬA: CHỈ SỬ DỤNG 1 BANNER DUY NHẤT + THÊM HIỆU ỨNG CHIỀU SÂU VÀO LOGIC BANNER - Đã sửa để nhận ảnh tự import */}
        {meaningStep === "single-flowers" && (
          <motion.div 
            style={{ ...styles.meaningHeroBanner1, backgroundImage: `url(${bannerYNghiaImg})` }} 
            initial={{ opacity: 0, scale: 1.02 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div style={styles.heroOverlayDark} />
            <motion.div 
              style={styles.heroTextLeft} 
              initial={{ opacity: 0, x: -40 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
            >
              <span className="luxury-sans" style={styles.heroMiniTag}>Bản nguyên | Tinh hoa tạo tác</span>
              <h2 className="luxury-serif" style={{ ...styles.meaningHeroTitle, fontSize: '48px', lineHeight: '1.2' }}> Nơi ngôn từ nhường chỗ<br/>Cho hương sắc bản nguyên </h2>
              <p className="luxury-sans" style={{ ...styles.meaningHeroSub, opacity: 0.85, maxWidth: '550px' }}> Chúng tôi lưu giữ những định nghĩa nguyên bản nhất về thông điệp của tự nhiên, chuyển hóa tinh tuyển thành ngôn ngữ thiết kế đương đại quý phái. </p>
            </motion.div>
          </motion.div>
        )}

        <div style={styles.header}>
          {meaningStep === "single-flowers" ? (
            <motion.button onClick={() => navigate("/mat-ngu-loai-hoa/theo-mau")} style={styles.backBtn} whileHover={{ x: 4 }}>
              <span className="luxury-sans">Khám phá theo sắc hoa</span>
              <ArrowLeft size={13} style={{ transform: "rotate(180deg)" }} />
            </motion.button>
          ) : (
            <motion.button onClick={() => setMeaningStep("single-flowers")} style={styles.backBtn} whileHover={{ x: -4 }}>
              <ArrowLeft size={13} />
              <span className="luxury-sans">Trở về Thư viện Cốt lõi</span>
            </motion.button>
          )}
          <h1 style={styles.title} className="luxury-serif">
            {meaningStep === "single-flowers" ? "Mật ngữ loài hoa" : ""}
          </h1>
          <div style={{ width: "100px" }} />
        </div>

        {/* BƯỚC 1: LƯỚI TỔNG HỢP CÁC LOÀI HOA ĐƠN NGHỆ THUẬT */}
        {meaningStep === "single-flowers" && (
          <div style={{ padding: "40px 0" }}>
            <div style={{ ...styles.collectionDivider, marginBottom: '40px' }}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText} className="luxury-sans">CHỌN MỘT LOÀI HOA ĐỂ LẮNG NGHE CÂU CHUYỆN</span>
              <div style={styles.dividerLine} />
            </div>
            <motion.div style={styles.singleFlowerGrid} variants={containerVariants} initial="hidden" animate="visible">
              {Object.keys(singleFlowerImages).map((key) => (
                <motion.div 
                  key={key} 
                  style={styles.singleFlowerCard} 
                  variants={itemVariants} 
                  onClick={() => { setChosenSingleFlowerKey(key); setMeaningStep("bouquet-list"); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
                  initial="initial" 
                  whileHover="hover" 
                  className="img-overflow-hidden smooth-transition"
                >
                  <motion.img src={singleFlowerImages[key]} alt={key} style={styles.singleFlowerImg} variants={{ initial: { scale: 1 }, hover: { scale: 1.06 } }} transition={{ duration: 0.8, ease: "easeOut" }} />
                  <motion.div style={{ ...styles.singleFlowerOverlay, display: "flex" }} variants={{ initial: { opacity: 0 }, hover: { opacity: 0.9 } }} transition={{ duration: 0.4 }} >
                    <div style={{ textAlign: "center", padding: "0 20px", zIndex: 2 }}>
                      <h2 style={styles.singleFlowerTitle} className="luxury-serif">{key}</h2>
                      <p style={styles.singleFlowerSub} className="luxury-sans">Khám phá ý nghĩa ẩn giấu ⟶</p>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        )}

        {/* BƯỚC 2: CHI TIẾT Ý NGHĨA VÀ LIST SẢN PHẨM SORT ĐỘNG THEO CHỦ THỂ MỞ RỘNG */}
        {meaningStep === "bouquet-list" && chosenSingleFlowerKey && (
          <div style={{ padding: "0 0 60px 0" }}>
            <div style={styles.meaningEditorialHeader}>
              <span className="luxury-sans" style={{ fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: '#999999' }}>Bản sắc vĩnh cửu</span>
              <h2 className="luxury-serif" style={{ fontSize: '42px', fontWeight: '300', margin: '15px 0' }}>{chosenSingleFlowerKey}</h2>
              <div style={styles.meaningDividerLine} />
              <p style={styles.editorialDesc} className="luxury-serif">
                {flowerMeaningsByType[chosenSingleFlowerKey]?.description || "Những đóa hoa nghệ thuật mang thông điệp thuần khiết, kiến tạo nên chuẩn mực mới cho quà tặng cảm xúc."}
              </p>
            </div>
            <div style={styles.collectionDivider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText} className="luxury-sans">BỘ SƯU TẬP {chosenSingleFlowerKey.toUpperCase()}</span>
              <div style={styles.dividerLine} />
            </div>
            <motion.div style={styles.bouquetGrid} variants={containerVariants} initial="hidden" animate="visible" key={`grid-${chosenSingleFlowerKey}`}>
              {currentTypeFlowers.length > 0 ? (
                currentTypeFlowers.map((hoa) => (
                  <motion.div 
                    key={hoa["Mã hoa"]} 
                    style={styles.studioCard} 
                    variants={itemVariants} 
                    onClick={() => openPopup(hoa)} 
                    onMouseEnter={() => setHoveredId(hoa["Mã hoa"])} 
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <div style={styles.cardImgContainer} className="img-overflow-hidden">
                      <motion.img
                        src={`/flowers/${hoa["Mã hoa"]}`}
                        alt={hoa["Tên hoa"]}
                        style={{ ...styles.cardImg, objectFit: "contain", width: "100%", height: "100%", padding: "24px", boxSizing: "border-box" }}
                        animate={{ scale: hoveredId === hoa["Mã hoa"] ? 1.04 : 1 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                      />
                      <div style={styles.cardBadge} className="luxury-sans"> {chosenSingleFlowerKey} </div>
                      <AnimatePresence>
                        {hoveredId === hoa["Mã hoa"] && (
                          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} style={styles.cardHoverOverlay}>
                            <motion.div initial={{ y: 15 }} animate={{ y: 0 }} exit={{ y: 10 }} transition={{ duration: 0.4, ease: "easeOut" }}>
                              <h4 style={styles.hoverProductName} className="luxury-serif">{hoa["Tên hoa"]}</h4>
                              <div style={styles.hoverPriceContainer}>
                                <span style={styles.hoverPrice} className="luxury-sans">{formatPrice(hoa["Giá bán"])}</span>
                              </div>
                              <p style={styles.hoverCta} className="luxury-sans">Chiêm ngưỡng tác phẩm ✧</p>
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                ))
              ) : (
                <p style={styles.emptyText} className="luxury-sans">Floraison đang thiết kế thêm các mẫu sản phẩm mới chứa thành phần này.</p>
              )}
            </motion.div>

            {/* KHỐI GRID TỔNG HỢP CUỐI TRANG CHO TAB Ý NGHĨA QUAY LẠI DANH SÁCH NHANH */}
            <div style={{ ...styles.appleSummaryBlock, marginTop: '100px' }}>
              <h3 className="luxury-serif" style={styles.summarySectionTitle}>LỰA CHỌN MẬT NGỮ LOÀI HOA KHÁC</h3>
              <div style={styles.miniFlowerThumbGrid}>
                {Object.keys(singleFlowerImages).map((key) => (
                  <button 
                    key={key} 
                    onClick={() => { setChosenSingleFlowerKey(key); window.scrollTo({ top: 450, behavior: 'smooth' }); }} 
                    style={{ ...styles.miniFlowerThumbBtn, borderColor: chosenSingleFlowerKey === key ? '#111111' : '#eeeeee', opacity: chosenSingleFlowerKey === key ? 1 : 0.6 }}
                  >
                    <img src={singleFlowerImages[key]} alt={key} style={styles.miniThumbImg} />
                    <span className="luxury-sans" style={styles.miniThumbText}>{key.replace("Hoa ", "")}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    )}

    {/* ========================================== MODAL CHI TIẾT SẢN PHẨM PREMIUM CLOSURE ========================================== */}
    <AnimatePresence>
      {selectedFlower && (
        <motion.div style={styles.modalBackdrop} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closePopup}>
          <motion.div 
            style={styles.modalContent} 
            className="custom-scrollbar" 
            initial={{ scale: 0.98, opacity: 0, y: 30 }} 
            animate={{ scale: 1, opacity: 1, y: 0 }} 
            exit={{ scale: 0.98, opacity: 0, y: 30 }} 
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} 
            onClick={(e) => e.stopPropagation()}
          >
            <button style={styles.closeBtn} onClick={closePopup}><X size={16} strokeWidth={1.5} /></button>
            <div style={styles.modalLayout}>
              <div style={styles.modalImageColumn}>
                <img src={`/flowers/${selectedFlower["Mã hoa"] || selectedFlower.image}`} alt={selectedFlower["Tên hoa"] || selectedFlower.name} style={styles.modalImg} />
              </div>
              <div style={styles.modalInfoColumn}>
                <span style={styles.modalMetaTag} className="luxury-sans"> {selectedFlower["Hoa ở mục nào"] || "Mật ngữ loài hoa thiết kế"} </span>
                <h2 style={styles.modalTitle} className="luxury-serif"> {selectedFlower["Tên hoa"] || selectedFlower.name} </h2>
                <p style={styles.modalDesc} className="luxury-serif"> {selectedFlower["Ý nghĩa của hoa"] || selectedFlower.description} </p>
                <div style={styles.metaSpecsWrapper} className="luxury-sans">
                  <div style={styles.specRow}>
                    <span style={styles.specLabel}>Thành phần:</span>
                    <span style={styles.specValue}>{selectedFlower["Thành phần hoa"] || chosenSingleFlowerKey || "Premium Florals"}</span>
                  </div>
                  <div style={styles.specRow}>
                    <span style={styles.specLabel}>Tone màu:</span>
                    <span style={styles.specValue}>{selectedFlower["Tông màu hoa"] || "Phối sắc nghệ thuật"}</span>
                  </div>
                  <div style={styles.specRow}>
                    <span style={styles.specLabel}>Dịp phù hợp:</span>
                    <span style={styles.specValue}>{selectedFlower["Dịp gửi hoa phù hợp"] || "Kỷ niệm, Chúc mừng, Tỏ tình"}</span>
                  </div>
                </div>
                <div style={styles.commitmentBox} className="luxury-sans">
                  <div style={styles.commitItem}>✦ Thiết kế độc bản & Giao hỏa tốc trong vòng 2 giờ nội thành.</div>
                  <div style={styles.commitItem}>✦ Chụp hình nghiệm thu sản phẩm thực tế trước khi gửi trao.</div>
                </div>
                <div style={styles.modalPrice} className="luxury-sans">
                  {formatPrice(selectedFlower["Giá bán"] || selectedFlower.price)}
                </div>
                <motion.button 
                  style={styles.addToCartBtn} 
                  onClick={() => addToCart(selectedFlower)} 
                  whileHover={{ backgroundColor: "#000000" }} 
                  whileTap={{ scale: 0.99 }} 
                  className="luxury-sans"
                >
                  Thêm vào giỏ hàng
                </motion.button>
                <div style={styles.accordionContainer} className="luxury-sans">
                  <div style={styles.accordionItem}>
                    <button style={styles.accordionHeader} onClick={() => setOpenSection(openSection === "bao-quan" ? "" : "bao-quan")}>
                      <span>Triết lý Chăm sóc Hoa</span>
                      {openSection === "bao-quan" ? <Minus size={11} /> : <Plus size={11} />}
                    </button>
                    <AnimatePresence>
                      {openSection === "bao-quan" && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
                          <div style={styles.accordionContent}> Tránh ánh nắng mặt trời và gió thổi trực tiếp. Thay nước mát sạch định kỳ mỗi ngày và cắt gốc xiên một góc 45 độ để hoa hấp thụ dưỡng chất tốt nhất. Đảm bảo độ bền tươi từ 3 - 5 ngày. </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <div style={styles.accordionItem}>
                    <button style={styles.accordionHeader} onClick={() => setOpenSection(openSection === "giao-nhan" ? "" : "giao-nhan")}>
                      <span>Giao nhận & Đổi trả</span>
                      {openSection === "giao-nhan" ? <Minus size={11} /> : <Plus size={11} />}
                    </button>
                    <AnimatePresence>
                      {openSection === "giao-nhan" && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
                          <div style={styles.accordionContent}> Vì tính chất hoa tự nhiên đổi mới theo mùa, cấu trúc sản phẩm có thể dịch chuyển nhẹ khoảng 5-10% nhưng tổng thể thẩm mỹ cao cấp luôn được cam kết trọn vẹn bởi nghệ nhân lành nghề. </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    <ToastNotification toast={toast} setToast={setToast} />
  </div>
);
}

// ========================================================================= 
// BRAND STYLES ARCHITECTURE
// ========================================================================= 
const styles = {
  container: { backgroundColor: "#ffffff", minHeight: "100vh", paddingBottom: "0px", color: "#111111" },
  header: { position: "relative", display: "flex", alignItems: "center", padding: "30px 6% 20px 6%", minHeight: "30px" },
  backBtn: { position: "absolute", left: "6%", display: "flex", alignItems: "center", gap: "10px", background: "none", border: "none", cursor: "pointer", fontSize: "13px", textTransform: "uppercase", letterSpacing: "2px", color: "#888888", transition: "color 0.3s ease", zIndex: 2 },
  title: { position: "absolute", left: 0, right: 0, top: "50%", transform: "translateY(-50%)", fontSize: "34px", fontWeight: "300", letterSpacing: "3px", textAlign: "center", color: "#111111", textTransform: "uppercase", pointerEvents: "none", zIndex: 1 },
  // IMMERSIVE HERO BANNERS
  appleHeroBanner: { position: 'relative', width: '100%', height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e0e10', backgroundImage: `url(${storyDo})`, backgroundSize: 'cover', backgroundPosition: 'center', overflow: 'hidden' },
  meaningHeroBanner1: { position: 'relative', width: '100%', height: '70vh', display: 'flex', alignItems: 'center', backgroundColor: '#111', backgroundImage: `url(${bachHopImg})`, backgroundSize: 'cover', backgroundPosition: 'center' },
  meaningHeroBanner2: { position: 'relative', width: '100%', height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', backgroundColor: '#fcfcfc', backgroundImage: `url(${linhLanImg})`, backgroundSize: 'cover', backgroundPosition: 'center', borderTop: '1px solid #f5f5f5' },
  heroOverlayDark: { position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.15)' },
  heroOverlayLight: { position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.2)' },
  heroTextCenter: { position: 'relative', zIndex: 2, textAlign: 'center', color: '#ffffff', padding: '0 20px', maxWidth: '800px' },
  heroTextLeft: { position: 'relative', zIndex: 2, color: '#ffffff', padding: '0 8%', maxWidth: '650px' },
  heroTextRight: { position: 'relative', zIndex: 2, color: '#111111', padding: '0 8%', maxWidth: '650px', textAlign: 'right' },
  heroMiniTag: { fontSize: '11px', textTransform: 'uppercase', letterSpacing: '4px', display: 'block', marginBottom: '15px', color: '#d4af37', fontWeight: '500' },
  heroMainTitle: { fontSize: '56px', fontWeight: '300', letterSpacing: '4px', marginBottom: '20px', lineHeight: '1.15' },
  meaningHeroTitle: { fontSize: '42px', fontWeight: '300', letterSpacing: '2px', marginBottom: '20px', color: '#ffffff' },
  heroSubText: { fontSize: '15px', fontWeight: '300', letterSpacing: '1px', opacity: 0.85, lineHeight: '1.6' },
  meaningHeroSub: { fontSize: '14px', fontWeight: '300', letterSpacing: '1px', lineHeight: '1.7', opacity: 0.9 },
  
  // EDITORIAL LAYOUT (STORYTELLING CANVAS)
  storySectionBackground: { padding: "80px 6%", minHeight: "85vh", display: "flex", alignItems: "center" },
  editorialRow: { display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: "80px", width: "100%", alignItems: "center" },
  editorialTextCol: { display: "flex", flexDirection: "column", alignItems: "flex-start" },
  editorialSub: { fontSize: "11px", textTransform: "uppercase", letterSpacing: "5px", fontWeight: "600", marginBottom: "15px" },
  editorialTitle: { fontSize: "52px", fontWeight: "300", letterSpacing: "1px", marginBottom: "20px", lineHeight: "1.1" },
  editorialPoetry: { fontSize: "18px", lineHeight: "1.6", marginBottom: "30px", opacity: 0.8, fontWeight: "300" },
  meaningBlock: { marginBottom: "35px" },
  meaningText: { fontSize: "14.5px", lineHeight: "1.8", marginBottom: "16px", fontWeight: "300", textAlign: "justify" },
  suitableBox: { padding: "14px 20px", borderLeft: "2px solid", fontSize: "12.5px", lineHeight: "1.6", fontWeight: "400", margin: "25px 0 10px 0" },
  appleCtaBtn: { background: "none", border: "1px solid", padding: "12px 24px", fontSize: "11px", textTransform: "uppercase", letterSpacing: "2px", cursor: "pointer", marginTop: "15px", fontWeight: "500" },
  editorialImgCol: { width: "100%", display: "flex", justifyContent: "center" },
  editorialImgFrame: { width: "100%", aspectRatio: "4/5", boxShadow: "0 25px 55px rgba(0,0,0,0.06)" },
  editorialImg: { width: "100%", height: "100%", objectFit: "cover" },
  editorialImgPlaceholder: { width: "100%", height: "100%", backgroundColor: "#f2f2f2", display: "flex", alignItems: "center", justifyContent: "center" },
  editorialTag: { position: "absolute", bottom: "20px", right: "20px", fontSize: "9px", textTransform: "uppercase", letterSpacing: "3px", color: "#ffffff", backgroundColor: "rgba(0,0,0,0.3)", padding: "4px 10px" },
  
  // COLLECTION SEPARATOR
  collectionDivider: { display: "flex", alignItems: "center", justifyItems: "center", padding: "60px 6% 30px 6%", gap: "20px" },
  dividerLine: { flex: 1, height: "1px", backgroundColor: "#eaeaea" },
  dividerText: { fontSize: "13px", letterSpacing: "3px", color: "#999999", fontWeight: "400" },
  
  // STUDIO PRODUCT GRID ARCHITECTURE
  studioGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "3px", padding: "0 6% 80px 6%" },
  studioCard: { position: "relative", cursor: "pointer", backgroundColor: "#ffffff" },
  // 1. Chỉnh background ảnh Hoa Bó thành màu Đen
  cardImgContainer: { position: "relative", width: "100%", aspectRatio: "3/4", backgroundColor: "#000000" },
  // 2. Ép ảnh hoa object-fit: "contain"
  cardImg: { 
    width: "100%", 
    height: "100%", 
    objectFit: "contain",
    padding: "10px"
  },
  cardBadge: { position: "absolute", top: "15px", left: "15px", backgroundColor: "#111111", color: "#ffffff", padding: "5px 10px", fontSize: "8px", letterSpacing: "2px", textTransform: "uppercase" },
  cardHoverOverlay: { position: "absolute", inset: 0, backgroundColor: "rgba(18, 18, 18, 0.85)", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "30px", textAlign: "center", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" },
  hoverProductName: { color: "#ffffff", fontSize: "22px", fontWeight: "300", letterSpacing: "1px", marginBottom: "12px" },
  hoverPriceContainer: { display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "25px" },
  hoverPrice: { color: "#d4af37", fontSize: "14px", fontWeight: "400", letterSpacing: "0.5px" },
  hoverCta: { color: "#ffffff", fontSize: "10px", textTransform: "uppercase", letterSpacing: "2px", borderBottom: "1px solid #d4af37", paddingBottom: "3px" },
  emptyText: { gridColumn: "1 / -1", textAlign: "center", color: "#999999", padding: "60px 0", fontSize: "12px", letterSpacing: "1px" },

  // TABS & NAVIGATION BLOCKS AT BOTTOM
  appleSummaryBlock: { borderTop: '1px solid #eeeeee', padding: '60px 6% 80px 6%', textAlign: 'center', backgroundColor: '#fbfbfd' },
  summarySectionTitle: { fontSize: '24px', fontWeight: '300', letterSpacing: '1px', marginBottom: '30px', color: '#333333' },
  tabsWrapper: { display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "10px 30px" },
  tabBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "13px", letterSpacing: "1px", padding: "10px 0", position: "relative", textTransform: "capitalize" },
  activeTabLine: { position: "absolute", bottom: 0, left: 0, right: 0, height: "1.5px" },
  
  // VIEW 2 SPECIFIC: MEANING DIRECTORY (ĐÃ SỬA HOA ĐƠN KHÔNG CÒN HIỆN MỜ)
  meaningEditorialHeader: { textAlignment: 'center', maxWidth: '750px', margin: '0 auto', padding: '60px 20px 40px 20px', textAlign: 'center' },
  meaningDividerLine: { width: '40px', height: '1px', backgroundColor: '#d4af37', margin: '20px auto' },
  editorialDesc: { fontSize: "15px", lineHeight: "1.75", color: "#555555", fontWeight: "300", fontStyle: "italic" },
  singleFlowerGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "3px", padding: "0 6%" },
  singleFlowerCard: { position: "relative", aspectRatio: "1/1", cursor: "pointer", backgroundColor: "#000" },
  singleFlowerImg: { width: "100%", height: "100%", objectFit: "cover", opacity: 1 },
  singleFlowerOverlay: { position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center" },
  singleFlowerTitle: { color: "#ffffff", fontSize: "28px", fontWeight: "300", letterSpacing: "2px", marginBottom: "8px" },
  singleFlowerSub: { color: "#d4af37", fontSize: "10px", textTransform: "uppercase", letterSpacing: "2px" },
  bouquetGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "3px", padding: "0 6% 40px 6%" },
  miniFlowerThumbGrid: { display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '15px', marginTop: '20px' },
  miniFlowerThumbBtn: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', border: '1px solid', borderRadius: '40px', background: '#ffffff', cursor: 'pointer', transition: 'all 0.3s ease' },
  miniThumbImg: { width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' },
  miniThumbText: { fontSize: '11px', fontWeight: '400', color: '#111111', letterSpacing: '0.5px' },
  
  // HIGH-END LUXURY MODAL SPECIFICATION
  modalBackdrop: { position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.3)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" },
  modalContent: { backgroundColor: "#ffffff", width: "100%", maxWidth: "1040px", maxHeight: "90vh", overflowY: "auto", position: "relative", padding: "45px" },
  closeBtn: { position: "absolute", top: "24px", right: "24px", background: "none", border: "none", cursor: "pointer", color: "#111111", padding: "6px", zIndex: 10 },
  modalLayout: { display: "grid", gridTemplateColumns: "0.91fr 1.09fr", gap: "50px", alignItems: "start" },
  
  // CHỈNH SỬA TẠI ĐÂY: Đảm bảo ảnh trong Modal đồng bộ với danh sách sản phẩm ngoài Grid
  modalImageColumn: { 
    width: "100%", 
    aspectRatio: "3/4", 
    backgroundColor: "#000000" // Khung viền màu đen đồng bộ
  },
  modalImg: { 
    width: "100%", 
    height: "100%", 
    objectFit: "contain", // Co nhỏ ảnh lại nằm trọn trong khung, không bị cắt
    padding: "10px" // Bổ sung khoảng trống bao quanh tương tự studioCard
  },
  
  modalInfoColumn: { display: "flex", flexDirection: "column" },
  modalMetaTag: { fontSize: "9px", textTransform: "uppercase", letterSpacing: "3px", color: "#999999", marginBottom: "12px" },
  modalTitle: { fontSize: "36px", fontWeight: "300", color: "#111111", marginBottom: "18px", lineHeight: "1.2" },
  modalDesc: { fontSize: "14px", lineHeight: "1.8", color: "#444444", marginBottom: "26px", fontWeight: "300", textAlign: "justify" },
  metaSpecsWrapper: { borderTop: "1px solid #eee", paddingTop: "20px", marginBottom: "26px", display: "flex", flexDirection: "column", gap: "12px" },
  specRow: { display: "flex", fontSize: "12.5px" },
  specLabel: { width: "110px", color: "#888888", letterSpacing: "0.5px" },
  specValue: { color: "#111111", flex: 1, fontWeight: "400" },
  commitmentBox: { backgroundColor: "#fbfbfa", padding: "16px 20px", marginBottom: "26px", display: "flex", flexDirection: "column", gap: "8px", border: "1px solid #ededeb" },
  commitItem: { fontSize: "11.5px", color: "#555855", lineHeight: "1.5", letterSpacing: "0.2px" },
  modalPrice: { fontSize: "22px", color: "#111111", marginBottom: "20px", fontWeight: "400", letterSpacing: "0.5px" },
  addToCartBtn: { width: "100%", padding: "15px 0", backgroundColor: "#111111", color: "#ffffff", border: "none", fontSize: "11px", letterSpacing: "3px", textTransform: "uppercase", cursor: "pointer", transition: "background-color 0.3s ease", marginBottom: "26px", fontWeight: "500" },
  accordionContainer: { borderTop: "1px solid #eee" },
  accordionItem: { borderBottom: "1px solid #eee" },
  accordionHeader: { width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", background: "none", border: "none", cursor: "pointer", fontSize: "12px", color: "#111111", letterSpacing: '0.5px' },
  accordionContent: { paddingBottom: "16px", fontSize: "12px", lineHeight: "1.7", color: "#666666", fontWeight: "300" },
  toastWrapper: {
    position: "fixed",
    top: "80px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 10000,
    pointerEvents: "none"
  },
  toastContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "14px 28px",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(12px) saturate(180%)",
    WebkitBackdropFilter: "blur(12px) saturate(180%)",
    color: "#000000",
    pointerEvents: "auto",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.2)",
    borderRadius: "50px",
    border: "1px solid rgba(255, 255, 255, 0.25)",
    fontFamily: '"Montserrat", sans-serif',
    fontSize: "14px",
    fontWeight: "500",
    letterSpacing: "0.5px",
    maxWidth: "85vw",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: "1.4"
  },
};